import React from 'react';
import CreatableSelect from 'react-select/creatable';

const ComboBoxSelect = ({ 
  value, 
  onChange, 
  options = [], 
  placeholder = "Type to search or add custom options...",
  label,
  onCreateOption,
  isMulti = true,
  onInputChange = null, // New prop for handling search input
  isLoading = false, // New prop for loading state
  compact = false // New prop for compact mode
}) => {
  // Get brand colors from CSS variables
  const brandBlue = getComputedStyle(document.documentElement).getPropertyValue('--color-brand-blue').trim() || '#247AD4';
  const brandBlueLight = getComputedStyle(document.documentElement).getPropertyValue('--color-brand-blue-light').trim() || '#F1F7FF';

  // Custom styles for react-select
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: compact ? '38px' : '48px',
      padding: compact ? '2px 0px' : '4px 0px',
      borderRadius: '8px',
      fontSize: compact ? '14px' : '15px',
      border: state.isFocused ? `2px solid ${brandBlue}` : '1px solid #d1d5db',
      boxShadow: state.isFocused ? `0 0 0 3px ${brandBlueLight}` : 'none',
      transition: 'all 0.2s ease',
      '&:hover': {
        border: state.isFocused ? `2px solid ${brandBlue}` : '1px solid #9ca3af'
      }
    }),
    multiValue: (provided, { data }) => ({
      ...provided,
      backgroundColor: `${brandBlue}20`,
      border: `1px solid ${brandBlue}`,
      borderRadius: compact ? '4px' : '6px',
      padding: compact ? '1px 2px' : '2px 4px',
      margin: compact ? '1px' : '2px'
    }),
    multiValueLabel: (provided, { data }) => ({
      ...provided,
      color: brandBlue,
      fontWeight: '500',
      fontSize: compact ? '12px' : '14px',
      padding: compact ? '2px 4px' : '4px 6px'
    }),
    multiValueRemove: (provided, { data }) => ({
      ...provided,
      color: brandBlue,
      borderRadius: '0 6px 6px 0',
      ':hover': {
        backgroundColor: `${brandBlue}40`,
        color: brandBlue
      }
    }),
    option: (provided, { data, isSelected, isFocused }) => {
      // Check if this is a "create" option (when data.__isNew__ is true)
      const isCreateOption = data && data.__isNew__;
      
      return {
        ...provided,
        padding: compact ? '8px 10px' : '10px 12px',
        fontSize: compact ? '14px' : '15px',
        borderRadius: '4px',
        margin: '2px 4px',
        transition: 'all 0.15s ease',
        backgroundColor: isCreateOption 
          ? (isSelected ? '#10b981' : isFocused ? '#d1fae5' : '#ecfdf5')
          : (isSelected ? brandBlue : isFocused ? brandBlueLight : 'white'),
        color: isCreateOption
          ? (isSelected ? 'white' : '#065f46')
          : (isSelected ? 'white' : '#374151'),
        fontWeight: isCreateOption ? '600' : '400',
        ':active': {
          backgroundColor: isCreateOption ? '#059669' : brandBlue
        }
      };
    },
    menu: (provided) => ({
      ...provided,
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    })
  };

  return (
    <div className="mb-6">
      {label && (
        <label className="block text-base font-medium text-gray-700 mb-3">
          {label}
        </label>
      )}
      <CreatableSelect
        isMulti={isMulti}
        value={value}
        onChange={onChange}
        options={options}
        styles={{
          ...customStyles,
          menuPortal: (base) => ({ ...base, zIndex: 9999 })
        }}
        placeholder={placeholder}
        formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
        noOptionsMessage={({ inputValue }) => 
          inputValue ? `No options found for "${inputValue}". Press Enter to add it.` : 'Type to add options...'
        }
        onCreateOption={onCreateOption}
        onInputChange={onInputChange}
        isLoading={isLoading}
        menuPortalTarget={document.body}
        menuPosition="fixed"
      />
    </div>
  );
};

export default ComboBoxSelect;
